import {
  Alert,
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Box, Grid, GridItem, Heading, HStack, VStack} from 'platform/foundation';

import {useState} from 'react';

import {getApiErrorMessages} from '@dms/api/utils';
import {
  GetServiceCaseIntegrationsEvaClaimServiceOrder,
  PostServiceCaseIntegrationsEvaClaimExportApiArg,
  usePostServiceCaseIntegrationsEvaClaimExportMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {downloadFile, handleApiError} from '@dms/shared';

import {getApiDateString} from 'shared/web';

import {DEALER_CLAIM_NUMBER_MAX_LENGTH} from '../constants/dealerClaimNumberMaxLength';
import {EvaClaimFormSchemaType, EvaClaimFormSchema} from '../schemas/EvaClaimFormSchema';
import {ServiceOrderFields} from './ServiceOrderFields';

interface EvaClaimProps {
  orders: GetServiceCaseIntegrationsEvaClaimServiceOrder[];
  serviceCaseId: string;
  defaultValues?: Partial<EvaClaimFormSchemaType>;
  dealerClaimNumberTooltip?: string | null;
  onClose: () => void;
}

export function EvaClaim(props: EvaClaimProps) {
  const [error, setError] = useState<string | string[]>([]);
  const [postServiceCaseIntegrationsEvaClaimExport, {isLoading}] =
    usePostServiceCaseIntegrationsEvaClaimExportMutation();

  const handleSubmit: FormSubmitHandler<EvaClaimFormSchemaType> = async (values) => {
    const submitData: PostServiceCaseIntegrationsEvaClaimExportApiArg = {
      serviceCaseId: props.serviceCaseId,
      body: {
        dealerClaimNumber: values.dealerClaimNumber,
        repairStartDate: getApiDateString(values.repairStartDate),
        mileageStart: values.mileageStart,
        repairEndDate: values.repairEndDate ? getApiDateString(values.repairEndDate) : null,
        mileageEnd: values.mileageEnd ?? null,
        claimText: values.claimText ?? null,
        orders: values.orders,
      },
    };

    await postServiceCaseIntegrationsEvaClaimExport(submitData)
      .unwrap()
      .then((result) => {
        downloadFile(result.link, {fileName: result.filename});
        props.onClose();
      })
      .catch((error: Error) =>
        handleApiError(error, {
          silent: true,
          callback: ({error}) => setError(getApiErrorMessages(error)),
        })
      );
  };

  const errors = Array.isArray(error) ? error : [error];

  return (
    <Form<EvaClaimFormSchemaType>
      experimentalZodSchema={EvaClaimFormSchema}
      onSubmit={handleSubmit}
      defaultValues={props.defaultValues}
    >
      {(control, formApi) => {
        const orders = formApi.watch('orders');
        const areAllOrdersUnselected = orders?.every((order) => !order.isSelected) ?? true;
        const isSubmitDisabled = !formApi.formState.isValid || areAllOrdersUnselected;

        return (
          <HStack spacing={4} width="100%">
            <ServiceOrderFields control={control} formApi={formApi} orders={props.orders} />
            <Box flex={2}>
              <Box width="100%">
                <VStack spacing={4}>
                  {errors.map((error) => (
                    <Alert key={error} type="inline" variant="error" message={error} />
                  ))}
                  <Heading size={2}>{i18n.t('entity.integration.labels.evaClaimTitle')}</Heading>
                  <Grid spacing={4} columns={2}>
                    <GridItem span={1}>
                      <FormField
                        control={control}
                        isRequired
                        isCounterVisible
                        maxLength={DEALER_CLAIM_NUMBER_MAX_LENGTH}
                        label={i18n.t('entity.integration.labels.dealerClaimNumber')}
                        name="dealerClaimNumber"
                        type="text"
                        tooltip={props.dealerClaimNumberTooltip}
                      />
                    </GridItem>
                  </Grid>
                  <Grid spacing={4} columns={2}>
                    <FormField
                      control={control}
                      isRequired
                      name="repairStartDate"
                      type="date"
                      label={i18n.t('entity.integration.labels.repairStartDate')}
                    />
                    <FormField
                      control={control}
                      isRequired
                      name="mileageStart"
                      type="number"
                      label={i18n.t('entity.integration.labels.mileageStart')}
                      suffix={i18n.t('general.metric.km')}
                    />
                    <FormField
                      control={control}
                      name="repairEndDate"
                      type="date"
                      label={i18n.t('entity.integration.labels.repairEndDate')}
                    />
                    <FormField
                      control={control}
                      name="mileageEnd"
                      type="number"
                      label={i18n.t('entity.integration.labels.mileageEnd')}
                      suffix={i18n.t('general.metric.km')}
                    />
                  </Grid>
                  <FormField
                    control={control}
                    name="claimText"
                    type="textarea"
                    label={i18n.t('entity.integration.labels.claimText')}
                  />
                  <ButtonGroup align="right">
                    <Button
                      title={i18n.t('general.actions.cancel')}
                      variant="secondary"
                      isDisabled={isLoading}
                      onClick={props.onClose}
                    />
                    <FormButton
                      control={control}
                      isDisabled={isSubmitDisabled || isLoading}
                      isLoading={isLoading}
                      title={i18n.t('general.actions.export')}
                      type="submit"
                    />
                  </ButtonGroup>
                </VStack>
              </Box>
            </Box>
          </HStack>
        );
      }}
    </Form>
  );
}
